import {
  Component,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { AppConstants } from 'src/app/app.constants';
import { DrugPricing, IPricingStatus, ITrialClaimRequest, ITrialClaimTransactionResponse, MemberDetails, PharmacyHours, PharmacyPricing } from '@interfaces';
import { environment } from '../../../../environments/environment';
import { RejectCodesProcessingService, TrialClaimsService } from '@core';
import { AppCenterAnalytics, AppInsightsService } from '@services';
import { combineLatest, defer, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';

@Component({
  selector: 'app-drug-details',
  templateUrl: './drug-details.component.html',
  styleUrls: ['./drug-details.component.scss'],
  standalone: false,
})
export class DrugDetailsComponent implements OnInit {
  @ViewChild('googleMapElement', { static: false }) googleMapElement!: any;
  days = AppConstants.DAYS;
  public mapId: string = environment.googleMapId;
  public showMemberResponsibilityNote$!: Observable<boolean>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: {
      drugName: string;
      quantity: string;
      daysSupply: string;
      drugPricing: PharmacyPricing;
      memberdetails: MemberDetails
    },
    private readonly rejectCodes: RejectCodesProcessingService,
    private readonly trialClaimsService: TrialClaimsService,
    private readonly appInsightService: AppInsightsService,
    private readonly dialogRef: MatDialogRef<DrugDetailsComponent>
  ) { }

  get pharmacyPricing(): PharmacyPricing {
    return this.data.drugPricing;
  }

  get brandPricing(): DrugPricing {
    return this.pharmacyPricing.pricings[0]?.pricing;
  }

  get genericPricing(): DrugPricing {
    return this.pharmacyPricing.pricings[0]?.genericAlternativePricing;
  }

  ngOnInit(): void {
    this.appInsightService.trackPageView(AppCenterAnalytics.DRUG_PRICING_DETAILS_VIEWED, {
      data: {
        drugName: this.data.drugName,
        ndc: this.data.drugPricing.pricings[0]?.pricing.ndc,
        pharmacyNpi: this.data.drugPricing.pharmacy.npi,
      },
    });
    this.loadPricings(this.brandPricing);
    this.loadPricings(this.genericPricing);
    this.showMemberResponsibilityNote$ = this.showMemberResponsibilityNote();
  }

  private loadPricings(pricing: DrugPricing): void {
    if (!pricing.rejectMessage) {
      pricing.memberResponsibilityEstimate$ = of({
        status: 'SUCCESS',
        success: true,
        patientPay: `${pricing.memberCost ?? '-'}`,
      } as IPricingStatus);
    } else {
      this.shouldReturnPrice(pricing.rejectMessage)
        .pipe(
          take(1),
          map((returnPrice) => {
            if (returnPrice) {
              pricing.memberResponsibilityEstimate$ = this.getPriceByTrialClaim(pricing)
                .pipe(
                  // Cache the latest value and share across subscribers
                  shareReplay({ bufferSize: 1, refCount: true })
                )
                ;
            } else {
              pricing.memberResponsibilityEstimate$ = of({
                status: 'HARD_REJECT',
                success: true,
                patientPay: `${pricing.memberCost ?? '-'}`,
              } as IPricingStatus);
            }
          })
        )
        .subscribe();
    }
  }

  public getDisplayRejectMessage(rejectMessage: string): Observable<string> {
    if (this.rejectCodes.isConcatenatedReject(rejectMessage)) {
      return of(
        "A clinical review will be required before you can fill your prescription."
      );
    } else {
      return this.rejectCodes.getDisplayRejectMessage(rejectMessage);
    }
  }

  public shouldReturnPrice(rejectMessage: string): Observable<boolean> {
    return this.rejectCodes.shouldReturnPrice(rejectMessage);
  }

  public close(): void {
    this.dialogRef.close();
  }

  public getDay(day: string): string {
    const val: string[] = this.data.drugPricing.pharmacy.hours[
      day as keyof PharmacyHours
    ] as string[];
    if (!val[0] || !val[1]) {
      return '-';
    }

    const str1 = (+val[0].split(':')[0] % 12).toString() || '12';
    const str2 = (+val[1].split(':')[0] % 12).toString() || '12';
    return str1 + ':00AM' + ' - ' + str2 + ':00PM';
  }

  private getPriceByTrialClaim(pricing: DrugPricing): Observable<IPricingStatus> {
    const request: ITrialClaimRequest = {
      cardholderId: this.data.memberdetails?.cardId,
      daysSupply: Number(this.data.daysSupply),
      decimalQuantity: Number(this.data.quantity),
      groupId: this.data.memberdetails?.clientId,
      ndc: pricing.ndc,
      patientFirstName: this.data.memberdetails?.firstName,
      patientLastName: this.data.memberdetails?.lastName,
      personCode: this.data.memberdetails?.personCode,
      serviceProviderId: this.pharmacyPricing.pharmacy.npi,
    };
    return this.trialClaimsService.runTrialClaim(request).pipe(
      take(1),
      switchMap((response) =>
        this.mapDrugPricingFromTrialClaimRun(response, request, pricing.rejectMessage)
      ),
      catchError((error) => {
        this.appInsightService.trackPageView(
          `${AppCenterAnalytics.DRUG_PRICING_TRIAL_CLAIMS_API_ERROR}`,
          { Response: error }
        );
        return of(error);
      })
    );
  }
  private mapDrugPricingFromTrialClaimRun(
    response: ITrialClaimTransactionResponse,
    request: ITrialClaimRequest,
    rejectMessage: string
  ): Observable<IPricingStatus> {
    return defer(() => {
      const patientPayStr = `${response.patientPay}`;

      // Log API success event
      this.logTrialClaimSuccessResponse(
        rejectMessage,
        request,
        patientPayStr,
        response.rejectCodes || []
      );

      if (response.rejectCodes?.length) {
        return this.rejectCodes.shouldReturnPriceDespiteRejects(response.rejectCodes).pipe(
          map((returnPrice) => {
            const formattedPatientPay = returnPrice ? patientPayStr : '';
            this.trackTrialClaimEvent(returnPrice, patientPayStr, response.rejectCodes);
            return {
              status: 'REJECT_WITH_RETURN_PRICE',
              success: returnPrice,
              patientPay: formattedPatientPay,
            } as IPricingStatus;
          })
        );
      } else {
        this.trackTrialClaimEvent(true, patientPayStr);
        return of({
          status: 'REJECT_WITH_RETURN_PRICE',
          success: true,
          patientPay: patientPayStr,
        } as IPricingStatus);
      }
    });
  }

  private trackTrialClaimEvent(success: boolean, patientPay?: string, rejectCodes?: string[]): void {
    if (success) {
      this.appInsightService.trackPageView(AppCenterAnalytics.DRUG_PRICING_TRIAL_CLAIM_RESOLVED, {
        data: { patientPay },
      });
    } else {
      this.appInsightService.trackPageView(AppCenterAnalytics.DRUG_PRICING_TRIAL_CLAIM_REJECTED, {
        data: { rejectCodes },
      });
    }
  }

  private logTrialClaimSuccessResponse(
    rejectMessage: string,
    request: ITrialClaimRequest,
    patientPay: string,
    rejectCodes: string[]
  ): void {
    this.appInsightService.trackPageView(
      `${AppCenterAnalytics.DRUG_PRICING_TRIAL_CLAIMS_API_SUCCESS}`,
      {
        data: {
          ndc: request.ndc,
          pharmacyNpi: request.serviceProviderId,
          daysSupply: request.daysSupply,
          quantity: request.decimalQuantity,
          apiRejectMessage: rejectMessage,
        },
        Response: {
          patientPay,
          rejectCodes,
        },
      }
    );
  }

  private showMemberResponsibilityNote(): Observable<boolean> {
    return combineLatest([
      this.genericPricing.memberResponsibilityEstimate$,
      this.brandPricing.memberResponsibilityEstimate$,
    ]).pipe(
      map(([genericPrice, brandPrice]) => {
        const bothHardReject =
          genericPrice?.status === 'HARD_REJECT' && brandPrice?.status === 'HARD_REJECT';

        const bothFailed =
          genericPrice?.status === 'REJECT_WITH_RETURN_PRICE' &&
          !genericPrice?.success &&
          brandPrice?.status === 'REJECT_WITH_RETURN_PRICE' &&
          !brandPrice?.success;
        return !bothHardReject && !bothFailed;
      })
    );
  }


  public cancel(): void {
    this.dialogRef.close();
  }
}
